<template>
  <section>
    <!-- Nuevo diseño -->
    <section id="document-workflow" class="scroll">

      <div class="position-wrapper">
        <div>
          <div class="position">
            <div class="participant">
              <span class="status-icon" v-if="createdBy.done"><i class="mdi mdi-check-circle-outline"></i></span>
              <div class="detail">
                <b-avatar :src="createdBy.avatar" :text="createdBy.initials" variant="primary"></b-avatar>
                <div class="user-info">
                  <span class="name" :title="createdBy.name">{{ createdBy.name }}</span>
                  <span class="action">Creador</span>
                </div>
              </div>
            </div>
          </div>
          <hr class="position-union">
        </div>
      </div>

      <template v-if="!hideCollaborators">
        <draggable
          v-if="collaborators.flat().length"
          v-model="collaborators"
          @start="drag=true" @end="drag=false"
          :disabled="!enableWorkflowModifications"
          animation="150"
          handle=".handle"
          class="position-wrapper"
        >
          <div v-for="(item, index) in collaborators" :key="index">
            <div class="position">
              <span class="handle" v-if="enableWorkflowModifications"><i class="mdi mdi-drag-vertical"></i></span>
              <draggable
                v-model="collaborators[index]"
                @start="drag=true" @end="drag=false"
                :sort="false"
                :disabled="!enableWorkflowModifications"
                animation="150"
                group="collaborators"
                filter=".filtered"
                class="position-grid"
                handle=".participant"
              >
                <template v-if="item.length">
                  <div v-for="(person, personIndex) in collaborators[index]" :key="person.id + personIndex" class="participant">
                    <span class="status-icon" v-if="person.done"><i class="mdi mdi-check-circle-outline"></i></span>
                    <span class="remove-icon" @click="showChangeEditorModal" v-if="enableWorkflowModifications"><i class="mdi mdi-close-circle-outline"></i></span>
                    <div class="detail">
                      <b-avatar :src="person.avatar" :text="person.initials" variant="primary"></b-avatar>
                      <div class="user-info">
                        <span class="name" :title="person.name">{{ person.name }}</span>
                        <span class="action">Editor</span>
                      </div>
                    </div>
                  </div>
                </template>
                <div v-else class="participant">
                  <span class="remove-icon" @click="collaborators.splice(index, 1)" v-if="enableWorkflowModifications"><i class="mdi mdi-close-circle-outline"></i></span>
                  <span class="name" style="display: block; width: auto;">Espacio vacío para editores</span>
                </div>
              </draggable>
              <a-tooltip :getPopupContainer="tooltipContainer">
                <template slot="title">
                  Agregar colaborador
                </template>
                <span class="append-icon" @click="showChangeEditorModal" v-if="enableWorkflowModifications">
                  <i class="mdi mdi-account-multiple-plus-outline"></i>
                </span>
              </a-tooltip>
            </div>
            <div class="append-space-icon" v-if="index === collaborators.length - 1 && enableWorkflowModifications">
              <span @click="collaborators.push([])" v-b-popover.hover.right="'Agregar espacio'"><i class="mdi mdi-plus-circle-outline"></i></span>
            </div>
            <hr class="position-union">
          </div>
        </draggable>
      </template>

      <template v-if="!hideParticipants">
        <draggable
          v-model="participants"
          @start="drag=true" @end="drag=false"
          :disabled="!enableWorkflowModifications"
          animation="150"
          handle=".handle"
          class="position-wrapper"
        >
          <div v-for="(item, index) in participants" :key="index">
            <div class="position">
              <span class="handle" v-if="enableWorkflowModifications"><i class="mdi mdi-drag-vertical"></i></span>
              <draggable
                v-model="participants[index]"
                :sort="false"
                @start="drag=true" @end="drag=false"
                :disabled="!enableWorkflowModifications"
                animation="150"
                :group="{
                  name: 'participants',
                  pull: mode !== 'sequential',
                  put: mode !== 'sequential'
                }"
                filter=".filtered"
                class="position-grid"
                :class="{ 'white-space-margin': !showBtnToAppendParticipant(index) && enableWorkflowModifications }"
              >
                <template v-if="item.length">
                  <div v-for="(person, personIndex) in participants[index]" :key="person.id + personIndex" class="participant">
                    <span v-b-popover.hover="'Documentación'" class="evicende-icon clickable" v-if="person.documentation" @click="onOpenDocumentation(person)"><i class="mdi mdi-file-document-outline"></i></span>
                    <span class="status-icon" v-if="person.done"><i class="mdi mdi-check-circle-outline"></i></span>
                    <span class="remove-icon" @click="onRemoveParticipant(index, personIndex)" v-if="enableWorkflowModifications && participants.length > 1"><i class="mdi mdi-close-circle-outline"></i></span>
                    <div class="detail">
                      <b-avatar :src="person.avatar" :text="person.initials" variant="primary"></b-avatar>
                      <div class="user-info">
                        <span class="name" :title="person.name">{{ person.name }}</span>
                        <lm-dropdown
                          closeAfterPick
                          hideSearchInput
                          :disabled="!enableWorkflowModifications || disableActionChange(person.id, index) || person.userType === 'inperson'"
                          :data-items="[
                            { text: $t('sign_action'), value: 'S' },
                            { text: $t('review_action'), value: 'R' }
                          ]"
                          @input="person.action = $event.value"
                        >
                          <span class="action">{{participanActionLabel(person)}}</span>
                        </lm-dropdown>
                      </div>
                    </div>
                    <span class="error-feedback" v-if="validateLastParticipantAction(person, index)">El último participante debe ser un firmante.</span>
                  </div>
                </template>
                <div v-else class="participant" style="height: auto">
                  <span class="remove-icon" @click="participants.splice(index, 1)" v-if="enableWorkflowModifications && participants.length > 1"><i class="mdi mdi-close-circle-outline"></i></span>
                  <span class="name" style="display: block; width: auto;">Espacio vacío para firmantes o revisores</span>
                </div>
              </draggable>
              <lm-dropdown
                v-if="showBtnToAppendParticipant(index) && enableWorkflowModifications"
                tooltipPosition="right"
                tooltip="Agregar Firmante o Revisor"
                closeAfterPick
                hideSearchInput
                :data-items="dropdownParticipantsIntemsData"
                :container="dropdownContainer"
                @input="onAppendNewParticipant(index, $event)"
              >
                <i class="mdi mdi-account-multiple-plus-outline" style="font-size: 1.5rem"></i>
              </lm-dropdown>
            </div>
            <hr v-if="index !== participants.length - 1" class="position-union">
            <div class="append-space-icon" v-else-if="index === participants.length - 1 && enableWorkflowModifications">
              <span @click="participants.push([])" v-b-popover.hover.right="'Agregar espacio'"><i class="mdi mdi-plus-circle-outline"></i></span>
            </div>
            <hr v-if="index === participants.length - 1 && enableWorkflowRecipients" class="position-union">
          </div>
        </draggable>
      </template>

      <div class="position-wrapper" v-if="enableWorkflowRecipients">
        <div>
          <div
            class="position"
            :class="{ 'white-space-margin': !enableWorkflowModifications }"
            style="margin-left: calc(24px + 0.5rem)"
          >
            <template v-if="recipients.length">
              <div v-for="(person, index) in recipients" :key="person.id + index" class="participant">
                <span class="status-icon" v-if="person.done"><i class="mdi mdi-check-circle-outline"></i></span>
                <span class="remove-icon" @click="recipients.splice(index, 1)" v-if="enableWorkflowModifications"><i class="mdi mdi-close-circle-outline"></i></span>
                <div class="detail">
                  <b-avatar :src="person.avatar" :text="person.initials" variant="primary"></b-avatar>
                  <div class="user-info">
                    <span class="name" :title="person.name">{{ person.name }}</span>
                    <span class="action">Destinatario</span>
                  </div>
                </div>
              </div>
            </template>
            <div v-else class="participant">
              <span v-if="document.isStarted" class="name">Sin destinatarios</span>
              <span v-else class="name">Agrega destinatarios</span>
            </div>
            <span v-if="enableWorkflowModifications" class="append-icon" v-b-modal.recipients-modal>
              <i class="mdi mdi-account-multiple-plus-outline"></i>
            </span>
          </div>
        </div>
      </div>

    </section>

    <b-modal
      id="recipients-modal"
      centered
      title="Agregar destinatarios"
      cancel-title="Cancelar"
      ok-title="Agregar"
      size="lg"
      @ok="onAddRecipient"
      @hidden="resetRecipient"
      v-if="enableWorkflowModifications && enableWorkflowRecipients"
    >
      <LmRadio
        v-model="recipientType"
        name="recipientType"
        label="Tipo de destinatario"
        :options="recipientToAppendOptions"
      />
      <template v-if="recipientType !== 'email'">
        <LmTreeSelect
          v-if="recipientType === 'subarea'"
          placeholder="Área"
          clearable
          pathToGetOptions="/customer/areas"
          v-model="areaModel"
        />
        <LmTreeSelect
          :key="recipientType"
          :placeholder="recipientToAppendOptions.find(el => el.value === recipientType).text"
          clearable
          :pathToGetOptions="recipientsOptions"
          v-model="recipient"
          :childrenOf="subareaChildrenOf"
        />
      </template>
      <LmEmailInput
        v-else
        :placeholder="recipientToAppendOptions.find(el => el.value === recipientType).text"
        v-model="$v.recipient.$model"
        :v="val('recipient')"

      />
    </b-modal>

    <b-modal
      id="external-modal"
      centered
      :title="isUserType === 'internal' ? 'Agregar un participante interno' : 'Agregar un participante externo'"
      cancel-title="Cancelar"
      :ok-title="isUserType === 'internal' ? 'Agregar interno' : 'Agregar'"
      size="lg"
      @ok="onAddExternal"
      @cancel="resetExternal"
      @hidden="resetExternal"
      v-if="enableExternal && enableWorkflowModifications"
    >
      <b-row>
        <div class="col-12 d-flex align-items-end">
          <LmEmailInput
            class="w-100"
            placeholder="Email"
            label="Email"
            v-model="$v.external.email.$model"
            :v="val('external.email')"
          />
          <LmButton
            class="ml-2"
            @click="onSearchExternal"
            buttonClass="btn-primary"
            value="Buscar usuario"
            :disabled="!external.email"
          />
        </div>
        <LmTextInput
          class="col-md-4 col-12"
          placeholder="Nombre"
          label="Nombre"
          v-model="$v.external.firstname.$model"
          :v="val('external.firstname')"
          :disabled="needToSearch"
        />
        <LmTextInput
          class="col-md-4 col-12"
          placeholder="Apellido"
          label="Apellido"
          v-model="$v.external.lastname.$model"
          :v="val('external.lastname')"
          :disabled="needToSearch"
        />
        <LmNumberInputV2
          class="col-md-4 col-12"
          placeholder="DNI"
          label="DNI"
          v-model="$v.external.dni.$model"
          :v="val('external.dni')"
          :disabled="needToSearch"
        />
      </b-row>
    </b-modal>

    <a-modal
      v-if="enableInPerson && !hideInPerson"
      v-model="showInPersonModal"
      width="65%"
      :okText="$t('add')"
      :okButtonProps="{
        props: {
          disabled: $v.inPerson.$invalid
        }
      }"
      :cancelText="$t('cancel')"
      @ok="onAppendInPerson"
      @cancel="resetInPerson"
    >
      <b-row>
        <div class="col-12 d-flex align-items-end">
          <LmEmailInput
            class="w-100"
            placeholder="Email"
            label="Email"
            v-model="$v.inPerson.email.$model"
            :v="val('inPerson.email')"
          />
          <LmTextInput
            class="col-md-4 col-12"
            placeholder="En carácter de"
            label="En carácter de"
            :v="val('inPerson.inCharacterOf')"
            v-model="inPerson.inCharacterOf"
          />
        </div>
        <LmTextInput
          class="col-md-4 col-12"
          placeholder="Nombre"
          label="Nombre"
          v-model="$v.inPerson.firstname.$model"
          :v="val('inPerson.firstname')"
        />
        <LmTextInput
          class="col-md-4 col-12"
          placeholder="Apellido"
          label="Apellido"
          v-model="$v.inPerson.lastname.$model"
          :v="val('inPerson.lastname')"
        />
        <LmNumberInputV2
          class="col-md-4 col-12"
          placeholder="DNI"
          label="DNI"
          v-model="inPerson.dni"
        />
      </b-row>
    </a-modal>

    <b-modal
      id="users-datatable-modal"
      size="xl"
      @ok="onConfirmParticipants"
      :cancel-title="$t('cancel')"
      :ok-title="$t('confirm')"
      scrollable
    >
      <LmDataTable
        @onSelect="onPickParticipant"
        slug="usuarios-todos-grupos-modal"
        hideButtons
      />
    </b-modal>

    <b-modal
      id="documentation-modal"
      size="xl"
      hide-footer
      scrollable
      title="Registro de firma"
    >
      <div class="d-flex align-items-center">
        <b-button
          class="position-absolute prev-btn"
          variant="secondary"
          @click="onPrevDocumentation"
          :disabled="disablePrevBtn"
        >
          <i class="mdi mdi-chevron-left"></i>
        </b-button>
        <iframe id="documentation-iframe" :src="documentationSrc(documentation.documentPath)" width="100%" :height="700"/>
        <b-button
          class="position-absolute next-btn"
          variant="secondary"
          @click="onNextDocumentation"
          :disabled="disableNextBtn"
        >
          <i class="mdi mdi-chevron-right"></i>
        </b-button>
      </div>
    </b-modal>
  </section>
</template>

<script>
// Components
import LmDataTable from '@/components/lmDataTable/table'
import LmTextInput from '@/components/form/elements/LmTextInput'
import LmEmailInput from '@/components/form/elements/LmEmailInput'
import LmNumberInputV2 from '@/components/form/elements/LmNumberInputV2'
import LmTreeSelect from '@/components/form/elements/LmTreeSelect'
import LmRadio from '@/components/form/elements/LmRadio'
import LmButton from '@/components/form/elements/LmButton'
import LmDropdown from '@/components/LmDropdown'
import draggable from 'vuedraggable'
import lmToasts from '@/mixins/lmToasts.js'

// Utils
import {
  required,
  email
} from 'vuelidate/lib/validators'
import _ from 'lodash'
import commonsApi from '@/api/commons'

export default {
  mixins: [lmToasts],
  inject: ['document'],
  provide () {
    const context = {}
    for (const key in this.context) {
      Object.defineProperty(context, key, {
        enumerable: true,
        get: () => this.context[key]
      })
    }
    return { context }
  },
  components: {
    draggable,
    LmDropdown,
    LmDataTable,
    LmEmailInput,
    LmNumberInputV2,
    LmTreeSelect,
    LmRadio,
    LmTextInput,
    LmButton
  },
  props: {
    isOnEditMode: {
      type: Boolean,
      required: true
    },
    hideCollaborators: {
      type: Boolean,
      default: false
    },
    hideParticipants: {
      type: Boolean,
      default: false
    },
    formCollaborators: {
      type: Array,
      default: () => []
    },
    hideInPerson: {
      type: Boolean,
      default: true
    },
    value: {
      type: Array,
      required: true
    },
    canAddExternalParticipants: {
      type: Boolean,
      default: false
    }
  },
  validations: {
    external: {
      firstname: {
        required
      },
      lastname: {
        required
      },
      email: {
        email,
        required
      },
      dni: {
        required
      }
    },
    inPerson: {
      firstname: {
        required
      },
      lastname: {
        required
      },
      email: {
        email
      }
    },
    recipient: {
      email
    }
  },
  data () {
    return {
      showInPersonModal: false,
      positionToAppendParticipant: 0,
      position: 0,
      mode: 'sequential',
      participantsSelected: [],
      needToSearch: true,
      isUserType: null,
      external: {
        id: '',
        firstname: '',
        lastname: '',
        email: '',
        dni: ''
      },
      inPerson: {
        id: '',
        firstname: '',
        lastname: '',
        dni: '',
        email: '',
        inCharacterOf: ''
      },
      userCounter: 0,
      // hideCollaborators: false,
      creator: null,
      collaborators: [],
      participants: [],
      recipients: [],
      recipientType: 'user',
      areaModel: null,
      recipient: null,
      documentation: {}
    }
  },
  computed: {
    context () {
      return {
        areaModel: this.areaModel
      }
    },
    dropdownParticipantsIntemsData () {
      const items = [{ text: 'Interno', value: 'internal' }]
      if (this.enableExternal) {
        items.push({ text: 'Externo', value: 'external' })
      }
      if (this.enableInPerson && !this.hideInPerson) {
        items.push({ text: 'Presencial', value: 'inPerson' })
      }
      return items
    },
    subareaChildrenOf () {
      return this.recipientType === 'subarea' ? 'areaModel' : null
    },
    enableExternal () {
      return process.env?.VUE_APP_ENABLE_EXTERNAL_SIGNERS === 'yes' && this.canAddExternalParticipants
    },
    enableInPerson () {
      return process.env?.VUE_APP_ENABLE_IN_PERSON_PARTICIPANTS === 'yes'
    },
    enableWorkflowModifications () {
      return !this.document.isStarted
    },
    enableWorkflowRecipients () {
      return this.document.isComunicable
    },
    createdBy () {
      if (this.creator) {
        return this.creator
      }
      const user = this.$store.getters['session/userInfo']
      return {
        id: user.id,
        name: `${user.lastname}, ${user.firstname}`,
        initials: user.initials,
        avatar: user.avatar,
        action: 'C'
      }
    },
    recipientToAppendOptions () {
      let recipientOptions = [ { text: 'Usuario', value: 'user' } ]

      if (!(process.env?.VUE_APP_ENABLE_AREAS_AS_RECIPIENTS === 'no')) {
        recipientOptions.push({ text: 'Área', value: 'area' })
      }

      recipientOptions.push(
        { text: 'Subárea', value: 'subarea' },
        { text: 'Grupo', value: 'group' },
        { text: 'Email', value: 'email' }
      )

      return recipientOptions
    },
    recipientsOptions () {
      if (this.recipientType === 'user') {
        return '/users/all'
      }
      return `/customer/${this.recipientType}s`
    },
    participantsWithDocumentation () {
      return this.participants.flat().filter(el => el.documentation)
    },
    disablePrevBtn () {
      const index = this.participantsWithDocumentation.findIndex(el => el.id === this.documentation.id)
      return !this.participantsWithDocumentation[index - 1]
    },
    disableNextBtn () {
      const index = this.participantsWithDocumentation.findIndex(el => el.id === this.documentation.id)
      return !this.participantsWithDocumentation[index + 1]
    }
  },
  methods: {
    val (position) {
      return _.get(_.cloneDeep(this.$v), position)
    },
    tooltipContainer () {
      return this.$el
    },
    dropdownContainer () {
      return document.getElementById('document-workflow')
    },
    onAppendNewParticipant (index, event) {
      this.positionToAppendParticipant = index
      if (event.value === 'internal') {
        this.$bvModal.show('users-datatable-modal')
      }
      if (event.value === 'external') {
        this.$bvModal.show('external-modal')
      }
      if (event.value === 'inPerson') {
        this.showInPersonModal = true
      }
    },
    onOpenDocumentation (person) {
      this.documentation = person.documentation
      this.documentation.id = person.id
      this.$bvModal.show('documentation-modal')
    },
    documentationSrc (path) {
      return process.env?.VUE_APP_API_URL_STATIC_ASSETS + path
    },
    onNextDocumentation () {
      const index = this.participantsWithDocumentation.findIndex(el => el.id === this.documentation.id)
      const next = this.participantsWithDocumentation.at(index + 1)
      if (next) {
        this.documentation = next.documentation
        this.documentation.id = next.id
      }
    },
    onPrevDocumentation () {
      const index = this.participantsWithDocumentation.findIndex(el => el.id === this.documentation.id)
      const prev = this.participantsWithDocumentation.at(index - 1)
      if (prev) {
        this.documentation = prev.documentation
        this.documentation.id = prev.id
      }
    },
    showBtnToAppendParticipant (index) {
      if (this.mode === 'sequential') {
        return index === this.participants.length - 1
      }
      if (this.mode === 'parallel') {
        return true
      }
    },
    onRemoveParticipant (spaceIndex, personIndex) {
      this.participants[spaceIndex].splice(personIndex, 1)
      if (!this.participants[spaceIndex].length && this.participants.length > 1) {
        this.participants.splice(spaceIndex, 1)
      }
    },
    clearEmptyParticipants () {
      this.participants = this.participants.filter(item => item.length)
    },
    participanActionLabel (person) {
      if (person.action === 'S') {
        let signActionLabel = this.$t('sign_action')

        if (person?.userType === 'inperson') {
          signActionLabel = this.$t('sign_action_inperson')
        }
        return signActionLabel
      } else if (person.action === 'R') {
        return this.$t('review_action')
      }
    },
    validateLastParticipantAction (person, index) {
      if (index === this.participants.length - 1 && person.action === 'R') {
        return true
      }
      return false
    },
    disableActionChange (id, index) {
      for (const [positionIndex, position] of this.participants.entries()) {
        if (position.some(el => el.id === id && el.action === 'S' && positionIndex !== index)) {
          return true
        }
      }
      return false
    },
    defineParticipantAction (id) {
      const flatted = this.participants.flat()
      if (flatted.some(el => el.id === id)) {
        return 'R'
      }
      return 'S'
    },
    async loadUserInfo (id) {
      const res = await commonsApi.axiosGetWrapper(`/users/user-meta/${id}`)
      const action = this.defineParticipantAction(id)
      return {
        id: res.data.id,
        name: `${res.data.lastname}, ${res.data.firstname}`,
        initials: res.data.userInitials || null,
        avatar: res.data.profilePicture || null,
        action
      }
    },
    onPickParticipant (event) {
      this.participantsSelected = event.map(el => el.id)
    },
    async onConfirmParticipants () {
      this.clearEmptyParticipants()
      const inputData = []
      if (this.mode === 'sequential') {
        for (const item of this.participantsSelected) {
          const user = await this.loadUserInfo(item)
          inputData.push([user])
          this.participants.push([user])
        }
      } else if (this.mode === 'parallel') {
        for (const item of this.participantsSelected) {
          const user = await this.loadUserInfo(item)
          inputData.push(user)
        }
        this.participants[this.positionToAppendParticipant].push(inputData)
      }
      this.participantsSelected = []
    },
    onAppendInPerson () {
      const user = {
        ...this.inPerson,
        id: this.userCounter++,
        name: `${this.inPerson.firstname} ${this.inPerson.lastname}`,
        action: 'S',
        userType: 'inperson'
      }
      if (this.mode === 'sequential') {
        this.clearEmptyParticipants()
        this.participants.push([user])
      } else if (this.mode === 'parallel') {
        this.participants[this.positionToAppendParticipant].push(user)
      }
      this.resetInPerson()
      this.showInPersonModal = false
    },
    onAddExternal () {
      if (this.isUserType === 'internal') {
        this.addInternalUserTypeOnSearchExternal()
      } else {
        const user = {
          ...this.external,
          id: this.external.id || this.userCounter++,
          name: `${this.external.firstname} ${this.external.lastname} - Externo`,
          action: 'S',
          userType: 'external'
        }
        if (this.mode === 'sequential') {
          this.clearEmptyParticipants()
          this.participants.push([user])
        } else if (this.mode === 'parallel') {
          this.participants[this.positionToAppendParticipant].push(user)
        }
        this.resetExternal()
        this.$v.$reset()
        this.needToSearch = true
      }
    },
    async addInternalUserTypeOnSearchExternal () {
      try {
        const response = await commonsApi.axiosGetWrapper(`/users/find-external-user/${this.external.email}`)
        let action = 'S'
        if (this.value.flat().some(el => el.action === 'S' && el.id === response.data._id)) {
          action = 'R'
        }
        const user = {
          id: response.data._id,
          name: `${response.data.firstname} ${response.data.lastname}`,
          action
        }
        if (this.mode === 'sequential') {
          this.clearEmptyParticipants()
          this.participants.push([user])
        }
        this.resetExternal()
        this.$v.$reset()
        this.$bvModal.hide('external-modal')
      } catch (error) {
        console.log(error)
      }
    },
    async onSearchExternal () {
      try {
        const response = await commonsApi.axiosGetWrapper(`/users/find-external-user/${this.external.email}`)
        if (response.data) {
          if (response.data?.userType === 'external') {
            this.external.firstname = response.data.firstname
            this.external.lastname = response.data.lastname
            this.external.dni = response.data.dni
            this.external.id = response.data._id
            this.isUserType = 'external'
          } else if (response.data?.userType === 'internal') {
            this.lmModalWarningMessage({ resultTitle: 'El usuario que buscó es interno, desea agregarlo como tal?', resultSubtitle: 'Revise y haga click en agregar interno si está seguro.', modalClosable: false, modalKeyboard: false, zIndex: 10000 })
            this.isUserType = 'internal'
            this.external.firstname = ''
            this.external.lastname = ''
            this.external.dni = ''
          }
        } else {
          this.external.firstname = ''
          this.external.lastname = ''
          this.external.dni = ''
          this.needToSearch = false
        }
      } catch (error) {
        console.log(error)
      }
    },
    resetInPerson () {
      for (const key in this.inPerson) {
        this.inPerson[key] = ''
      }
      this.$v.$reset()
    },
    resetExternal () {
      for (const key in this.external) {
        this.external[key] = ''
      }
      this.$v.$reset()
    },
    async appendCollaborators (event) {
      event = [...new Map(event.map(item => [item['id'], item])).values()]

      // Cada vez que agrego nuevos colaboradores, reseteo las posiciones
      const collaborators = []
      for (const user of event) {
        let item = await this.loadUserInfo(user.id)
        item = Object.assign(item, user)
        collaborators.push(item)
      }
      this.collaborators = [collaborators]

      this.recipients = this.recipients.filter(recipient => {
        return collaborators.map(editor => editor.id).includes(recipient.id)
      })
    },
    async appendCreator (event) {
      const user = await this.loadUserInfo(event.id)
      user.action = 'C'
      this.creator = user
    },
    resetRecipient () {
      this.recipientType = 'user'
      this.areaModel = null
      this.recipient = null
    },
    async onAddRecipient () {
      let item
      if (this.recipientType === 'user') {
        item = await this.loadUserInfo(this.recipient?.id)
      } else if (this.recipientType === 'email') {
        item = {
          id: this.recipient,
          name: this.recipient
        }
      } else {
        item = { ...this.recipient }
      }
      item.type = this.recipientType
      item.action = 'recipient'
      this.recipients.push(item)
    },
    showChangeEditorModal () {
      this.lmModalInformationMessage({
        modalClosable: false,
        modalKeyboard: false,
        resultSubtitle: '',
        buttons: [
          {
            btnText: this.$t('cancel'),
            btnType: 'danger',
            btnGhost: true,
            btnCallback: () => {
              this.isProcessing = false
            }
          },
          {
            btnText: this.$t('continue'),
            btnCallback: () => {
              this.$emit('on-change-collaborator')
            }
          }
        ],
        message: this.$t('confirmations.remove_editor_confirmation')
      })
    },

    update () {
      const workflow = _.cloneDeep(this.value)
      this.participants = []
      this.collaborators = []
      this.recipients = []
      this.creator = null
      workflow.forEach(position => {
        if (position.every(el => el.action === 'S' || el.action === 'R')) {
          this.participants.push(position)
        }
        if (position.every(el => el.action === 'E')) {
          this.collaborators.push(position)
        }
        if (position.some(el => el.action === 'C')) {
          this.creator = position.at(0)
        }
        if (position.every(el => el.action === 'recipient')) {
          this.recipients = position
        }
      })
      if (!this.participants.length) {
        this.participants.push([])
      }
      if (!this.collaborators.length) {
        this.collaborators.push([])
      }
    }
  },

  watch: {
    formCollaborators: {
      // immediate: true,
      deep: true,
      handler (val) {
        this.appendCollaborators(val)
      }
    },
    'recipientType': {
      handler (val) {
        this.recipient = null
      }
    }
  },

  created () {
    if (this.value?.flat()?.length) {
      const workflow = _.cloneDeep(this.value)
      workflow.forEach(position => {
        // console.log(position)
        if (position.every(el => el.action === 'S' || el.action === 'R')) {
          this.participants.push(position)
        }
        if (position.every(el => el.action === 'E')) {
          this.collaborators.push(position)
        }
        if (position.some(el => el.action === 'C')) {
          this.creator = position.at(0)
        }
        if (position.every(el => el.action === 'recipient')) {
          this.recipients = position
        }
      })
      this.clearEmptyParticipants()
      if (!this.participants.length) {
        this.participants.push([])
      }
      if (!this.collaborators.length) {
        this.collaborators.push([])
      }
    } else {
      this.participants.push([])
      this.collaborators.push([])

      const inputData = [[this.createdBy], ...this.collaborators, ...this.participants, this.recipients]
      this.$emit('input', inputData)
    }

    // Creo watchers que emiten el workflow actualizado
    this.$nextTick(() => {
      this.$watch('collaborators', val => {
        if (this.enableWorkflowRecipients) {
          let recipients = [...this.participants, ...val].flat().map(el => ({ ...el, type: 'user', action: 'recipient' }))
          recipients = this.recipients.concat(recipients)
          recipients = [...new Map(recipients.map(item => [item['id'], item])).values()]
          this.recipients = recipients
        }
        const inputData = [[this.createdBy], ...val, ...this.participants, this.recipients]
        this.$emit('input', inputData)
      }, { deep: true })

      this.$watch('participants', val => {
        if (this.enableWorkflowRecipients) {
          let recipients = [...this.collaborators, ...val].flat().map(el => ({ ...el, type: 'user', action: 'recipient' }))
          recipients = [].concat(this.recipients, recipients)
          recipients = recipients.filter((r) => {
            return r.userType !== 'inperson' || (r.userType === 'inperson' && r.email)
          })
          recipients = [...new Map(recipients.map(item => [item['id'], item])).values()]
          this.recipients = recipients
        }
        const inputData = [[this.createdBy], ...this.collaborators, ...val, this.recipients]
        this.$emit('input', inputData)
      }, { deep: true })

      if (this.enableWorkflowRecipients) {
        this.$watch('recipients', val => {
          val = [...new Map(val.map(item => [item['id'], item])).values()]
          const inputData = [[this.createdBy], ...this.collaborators, ...this.participants, val]
          this.$emit('input', inputData)
        }, { deep: true })
      }
    })
  }
}
</script>

<style lang="sass" scoped>
#document-workflow
  max-height: 800px
  display: flex
  flex-direction: column
  align-items: center
  .position-wrapper
    display: flex
    flex-direction: column
    display: flex
    column-gap: 1rem
  .position
    display: flex
    align-items: center
    justify-content: center
    column-gap: .5rem
    &.white-space-margin
      margin-right: calc(24px + 0.5rem)
    & > .position-grid
      display: flex
      gap: 1rem
    & .white-space-margin
      margin-right: calc(24px + 0.5rem)
    & .handle, & .append-icon
      font-size: 1.5rem
    & .append-icon
      cursor: pointer
  .participant
    width: 250px
    position: relative
    display: flex
    flex-direction: column
    border-radius: 8px
    background-color: #F6F6F6
    padding: 20px
    font-size: 1rem
    & > .detail
      display: flex
      align-items: center
      column-gap: .7rem
      & > .user-info
        flex: 1 1 100%
        display: flex
        flex-direction: column
    & > .error-feedback
      margin-top: 5px
      color: #FF5A5A
      font-size: .75rem
    & .name
      width: 160px
      overflow: hidden
      text-overflow: ellipsis
      display: -webkit-box
      line-clamp: 1
      -webkit-line-clamp: 1
      -webkit-box-orient: vertical
    & > .status-icon, & > .remove-icon, & > .evicende-icon
      position: absolute
      font-size: 24px
      top: -20px
      right: -12px
      padding: 2px
      border-radius: 8px
    & > .evicende-icon
      right: 15px
    & > .status-icon
      color: #00A9B4
    & > .remove-icon
      color: #FF5A5A
      cursor: pointer
      & > i
        vertical-align: initial
    & .action
      font-size: .8rem
  .position-union
    margin-top: 35px
    margin-bottom: 35px
    width: 50px
    height: 3px
    background-color: #007BFF
    transform: rotate(90deg)
  .append-space-icon
    display: flex
    justify-content: center
    & > span
      font-size: 1.5rem
      cursor: pointer

.scroll
  padding: 2rem 0 5rem 0
  overflow-x: auto
  &::-webkit-scrollbar
    height: 4px
    width: 4px
  &::-webkit-scrollbar-thumb
    border-radius: 20px
    background: #cccccc
  &::-webkit-scrollbar-thumb:hover
    background: #a8a8a8
.action-item
  cursor: pointer
  &:hover
    background: #ededed
.next-btn, .prev-btn
  color: white
  font-size: 1.2rem
.prev-btn
  left: 10px
.next-btn
  right: 10px
</style>
